import React, { useState, useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { Card, CardHeader, CardContent } from "@/shared/components/ui/Card";
import { PerformanceChartProps, DashboardPeriodMode } from "../types";

/**
 * Series de dados do gráfico com paleta semântica (mockup dashboard-atendimento)
 * Atendimentos = roxo, Leads = azul, Reuniões = âmbar, Pedidos = verde
 */
const dataSeries = [
  { key: "atendimentos", label: "Atendimentos", color: "var(--theme-primary)" },
  { key: "leads", label: "Leads", color: "var(--theme-info-color)" },
  { key: "reunioes", label: "Reuniões", color: "var(--theme-warning-color)" },
  { key: "pedidos", label: "Pedidos", color: "var(--theme-success-color)" },
] as const;

/**
 * Formata o label do eixo X baseado no período
 * - week: nome do dia (seg, ter, qua...)
 * - month/year/custom: data DD/MM
 */
function formatDateLabel(dateStr: string, period: DashboardPeriodMode): string {
  const date = new Date(dateStr + 'T12:00:00');
  if (period === 'week') {
    return date.toLocaleDateString('pt-BR', { weekday: 'short' });
  }
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  return `${day}/${month}`;
}

/**
 * Tooltip customizado always-light com visual premium
 */
const CustomTooltip = ({ active, payload, label, period }: any) => {
  if (active && payload && payload.length) {
    // Calcula o total empilhado
    const total = payload.reduce((sum: number, entry: any) => sum + (entry.value || 0), 0);
    // Formata o label da data (label aqui é o date YYYY-MM-DD)
    const displayLabel = label ? formatDateLabel(label, period || 'week') : '';
    return (
      <div
        className="rounded-xl border px-4 py-3"
        style={{
          backgroundColor: '#ffffff',
          borderColor: '#e5e7eb',
          boxShadow: '0 10px 25px -5px rgba(0,0,0,0.1), 0 4px 6px -4px rgba(0,0,0,0.05)',
        }}
      >
        <p
          className="font-semibold text-sm mb-2"
          style={{ color: '#1e293b' }}
        >
          {displayLabel}
        </p>
        <div className="space-y-1.5">
          {payload.map((entry: any, index: number) => {
            const series = dataSeries.find(s => s.key === entry.dataKey);
            return (
              <div key={index} className="flex items-center justify-between gap-6">
                <div className="flex items-center gap-2">
                  <div
                    className="w-2 h-2 rounded-full"
                    style={{ backgroundColor: entry.color }}
                  />
                  <span className="text-xs" style={{ color: '#64748b' }}>
                    {series?.label || entry.dataKey}
                  </span>
                </div>
                <span className="text-xs font-semibold" style={{ color: '#1e293b' }}>
                  {entry.value}
                </span>
              </div>
            );
          })}
          {payload.length > 1 && (
            <div
              className="flex items-center justify-between gap-6 pt-1.5 mt-1 border-t"
              style={{ borderColor: '#e5e7eb' }}
            >
              <span className="text-xs font-semibold" style={{ color: '#1e293b' }}>
                Total
              </span>
              <span className="text-xs font-bold" style={{ color: '#1e293b' }}>
                {total}
              </span>
            </div>
          )}
        </div>
      </div>
    );
  }
  return null;
};

/**
 * Componente de gráfico de performance do dashboard
 *
 * Exibe um gráfico de barras empilhadas mostrando
 * atendimentos, leads, reuniões e pedidos ao longo do tempo.
 * Uma barra por dia com diferentes cores para cada tipo.
 * Series podem ser ativadas/desativadas clicando na legenda.
 */
export const PerformanceChart: React.FC<PerformanceChartProps> = ({
  data,
  period = 'week',
  height = 300,
}) => {
  const [hiddenSeries, setHiddenSeries] = useState<Set<string>>(new Set());

  // Usar `date` (YYYY-MM-DD, único) como key em vez de label formatado
  // Labels formatados podem ter duplicatas (ex: dois "sáb." na semana)
  // que causam recharts resolver tooltip para o primeiro match
  const chartData = useMemo(() => data.map((item) => ({
    date: item.date,
    atendimentos: item.atendimentos,
    leads: item.leads,
    reunioes: item.reunioes,
    pedidos: item.pedidos,
  })), [data]);

  const toggleSeries = (key: string) => {
    setHiddenSeries(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  };

  // Formata tick do eixo X usando o period atual
  const formatTick = (dateStr: string) => formatDateLabel(dateStr, period);

  // Para mês/ano com muitos dias, reduzir font e mostrar ticks alternados
  const showAllTicks = chartData.length <= 14;

  return (
    <Card
      className="h-full"
      style={{
        backgroundColor: 'var(--theme-card-background)',
        borderColor: 'var(--theme-card-border)',
      }}
    >
      <CardHeader
        className="py-3.5 px-4 border-b"
        style={{ borderColor: 'var(--theme-card-border)' }}
      >
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <h3
              className="text-sm font-bold tracking-tight"
              style={{ color: 'var(--theme-card-text)' }}
            >
              Volume por dia
            </h3>
            <p
              className="text-xs mt-0.5"
              style={{ color: 'var(--theme-muted-text)' }}
            >
              Atendimentos, leads, reuniões e pedidos
            </p>
          </div>

          {/* Legend inline no header (clicável — toggle de série) */}
          <div className="flex items-center flex-wrap gap-x-3 gap-y-1 justify-end">
            {dataSeries.map(({ key, label, color }) => {
              const isHidden = hiddenSeries.has(key);
              return (
                <button
                  key={key}
                  data-testid={`chart-legend-${key}`}
                  className="flex items-center gap-1.5 text-xs font-medium transition-opacity cursor-pointer select-none"
                  style={{
                    color: 'var(--theme-muted-text)',
                    opacity: isHidden ? 0.35 : 1,
                  }}
                  onClick={() => toggleSeries(key)}
                  title={isHidden ? `Mostrar ${label}` : `Ocultar ${label}`}
                >
                  <div
                    className="w-2 h-2 rounded-full flex-shrink-0"
                    style={{
                      backgroundColor: isHidden ? 'var(--theme-muted-text)' : color,
                    }}
                  />
                  <span>{label}</span>
                </button>
              );
            })}
          </div>
        </div>
      </CardHeader>
      <CardContent className="px-2 pb-4 pt-4">
        <ResponsiveContainer width="100%" height={height}>
          <BarChart
            data={chartData}
            margin={{ top: 10, right: 10, left: -10, bottom: 0 }}
            barCategoryGap={period === 'year' ? '10%' : '20%'}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--theme-card-border)"
              strokeOpacity={0.5}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatTick}
              tick={{ fontSize: chartData.length > 31 ? 9 : 11, fill: 'var(--theme-muted-text)' }}
              axisLine={false}
              tickLine={false}
              dy={8}
              interval={showAllTicks ? 0 : Math.floor(chartData.length / 15)}
              angle={chartData.length > 31 ? -45 : 0}
              textAnchor={chartData.length > 31 ? 'end' : 'middle'}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--theme-muted-text)' }}
              axisLine={false}
              tickLine={false}
              dx={-4}
            />
            <Tooltip
              content={<CustomTooltip period={period} />}
              cursor={{
                fill: 'var(--theme-button-ghost-hover)',
                opacity: 0.5,
              }}
            />
            {dataSeries.map(({ key, color }, index) => (
              <Bar
                key={key}
                dataKey={key}
                stackId="performance"
                fill={color}
                radius={index === dataSeries.length - 1 ? [4, 4, 0, 0] : [0, 0, 0, 0]}
                hide={hiddenSeries.has(key)}
              />
            ))}
          </BarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
};

PerformanceChart.displayName = 'PerformanceChart';
